"use client";

import { useCallback, useEffect, useState } from "react";
import { getMySubscription } from "@/utils/api";
import type { MeResponse, Tier } from "@/types/subscription";

export interface UseSubscriptionResult {
  tier: Tier | null;
  isPro: boolean;
  isAuthenticated: boolean;
  loading: boolean;
  /** Re-reads auth + refetches /subscriptions/me. The SubscriptionProvider
   *  calls this on route change and on the `auth-changed` event so the tier
   *  updates after login/logout without a full page reload. */
  refresh: () => void;
}

function hasToken(): boolean {
  if (typeof window === "undefined") return false;
  return !!(localStorage.getItem("token") || sessionStorage.getItem("token"));
}

/**
 * Read-only hook for the current user's subscription tier.
 *
 * - Re-reads the token on each fetch (NOT a one-time snapshot) so the result
 *   reflects login/logout that happens after mount. The provider drives the
 *   refresh; standalone use still fetches once on mount.
 * - Skips the fetch when there is no token (avoids the 401 getMySubscription
 *   throws when unauthenticated, per utils/api.js:543).
 * - Does NOT include the retry-on-incomplete logic from membership/page.tsx —
 *   that retry is checkout-specific (post-payment webhook race) and must stay
 *   in the membership page.
 */
export function useSubscription(): UseSubscriptionResult {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  const fetchMe = useCallback(async () => {
    const authed = hasToken();
    setIsAuthenticated(authed);
    if (!authed) {
      setMe(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data: MeResponse | null = await getMySubscription();
      setMe(data);
    } catch (err) {
      // T-124: only a genuine auth failure (401 — token expired/revoked)
      // should drop the tier. requireProOrPaywall gates purely on the client
      // isPro with no 401 interceptor to clear the token, so a dead session
      // must clear here. A transient/network/5xx error (err.response is
      // undefined for network errors, present-but-non-4xx for 5xx) preserves
      // the last-known-good tier — this is what makes the per-nav refetch
      // flicker-safe instead of wiping Pro to free on every blip. A 404 (no
      // subscription) never reaches this catch — it's already mapped to a
      // resolved null upstream (utils/api.js).
      //
      // A 403 on the user's OWN /subscriptions/me is deliberately treated as
      // ambiguous, NOT a genuine auth failure: it's far more likely an
      // edge-layer artifact (Cloudflare bot-challenge, WAF rule, per-IP rate
      // limit, geo rule) than a real permissions failure — this endpoint has
      // no per-user authorization logic to legitimately deny, only
      // authentication. So 403 now PRESERVES the tier, same as a 5xx or
      // network error. A truly revoked/expired token surfaces as 401, which
      // still clears.
      //
      // This fails OPEN by design on any ambiguous/unreadable error (no
      // `.response`, or a `.response.status` other than 401) — including the
      // case where a CORS-blocked request is indistinguishable client-side
      // from a genuine network outage. We deliberately do NOT add a
      // consecutive-failure counter to compensate: that would downgrade Pro
      // during a real offline stretch (no connectivity → every refetch fails
      // the same way a revoked token would look), reintroducing the exact
      // flicker/downgrade this fix exists to prevent. The actual entitlement
      // control is server-side, not this client heuristic — the backend MUST
      // enforce Pro entitlement on every protected resource independent of
      // what this hook believes, and GET /subscriptions/me MUST send CORS
      // headers on its 4xx responses so a genuine 401 is readable here
      // instead of failing open as an opaque network error.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        setMe(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const isPro = me?.tier === "pro" && me.membership?.status === "active";

  return {
    tier: me?.tier ?? null,
    isPro: !!isPro,
    isAuthenticated,
    loading,
    refresh: fetchMe,
  };
}
